import type { ContextEvents } from '@gzl10/nexus-sdk'
import type { TypedEventEmitter } from '@kernel/events/emitter.js'

/**
 * Creates the semantic event API (ctx.events) wrapping the raw EventEmitter2.
 *
 * Three patterns:
 * - notify: fire-and-forget (emit). Errors in listeners are caught and logged.
 * - query:  request data from 0..N listeners (emitAsync). Returns flat array.
 * - command: request action from 0..1 handler (emitAsync). Returns first result.
 */
export function createEventsApi(
  emitter: TypedEventEmitter,
  logger: { warn: (obj: object, msg: string) => void }
): ContextEvents {
  return {
    notify(event, payload) {
      try {
        if (payload !== undefined) {
          emitter.emit(event, payload)
        } else {
          emitter.emit(event)
        }
      } catch (err) {
        // LIMITATION: fire-and-forget contract — listener errors must not affect the emitter
        logger.warn({ event, err }, 'Event listener error (notify)')
      }
    },

    async query<T>(event: string, payload?: unknown): Promise<T[]> {
      const args = payload !== undefined ? [payload] : []
      const responses = await emitter.emitAsync(event, ...args)
      if (!responses?.length) return []
      // Flatten 1 level: if a listener returns an array, its elements are spread into the result
      return responses.flatMap(r =>
        r !== undefined ? (Array.isArray(r) ? r : [r]) : []
      ) as T[]
    },

    async command<T>(event: string, payload?: unknown): Promise<T | undefined> {
      const args = payload !== undefined ? [payload] : []
      const responses = await emitter.emitAsync(event, ...args)
      if (!responses?.length) return undefined
      const defined = responses.filter(r => r !== undefined)
      if (defined.length > 1) {
        logger.warn(
          { event, handlerCount: defined.length },
          'Multiple handlers responded to command event — using first'
        )
      }
      return defined[0] as T | undefined
    },

    on(event, listener) { emitter.on(event, listener as (...args: unknown[]) => void) },
    off(event, listener) { emitter.off(event, listener as (...args: unknown[]) => void) },
    once(event, listener) { emitter.once(event, listener as (...args: unknown[]) => void) },
    onAny(listener) { emitter.onAny(listener as (event: string | string[], ...args: unknown[]) => void) },
  }
}
